import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { v4 as uuid } from 'uuid';
import api from '../../api/workTasksApi';
import WorkTaskEditor from './WorkTaskEditor';
import { statuses, taskError } from './taskUtils';

export default function WorkTaskPanel({ source, relatedSources = [], initialTitle = '' }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editor, setEditor] = useState(null);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [legacy, setLegacy] = useState(null);
  const sequence = useRef(0);
  const key = JSON.stringify(source);
  const load = useCallback(async () => {
    const seq = ++sequence.current;
    setLoading(true); setError('');
    try {
      const caps = await api.capabilities();
      const result = await api.list({ view: 'related', source_kind: JSON.parse(key).kind, source_id: JSON.parse(key).source_id, page, page_size: 10 });
      const old = ['message', 'conversation'].includes(JSON.parse(key).kind) ? await api.legacy(JSON.parse(key)) : null;
      if (seq !== sequence.current) return;
      setPermissions(caps.permissions); setData(result); setLegacy(old);
    } catch (e) { if (seq === sequence.current) { setPermissions([]); setData({ items: [], total: 0 }); setError(taskError(e)); } }
    finally { if (seq === sequence.current) setLoading(false); }
  }, [key, page]);
  useEffect(() => {
    const activeRequests = sequence;
    load();
    return () => { ++activeRequests.current; };
  }, [load]);
  useEffect(() => { setPage(1); setEditor(null); }, [key]);
  const findTasks = async () => {
    try { setCandidates((await api.list({ view: permissions.includes('work_tasks.read_team') ? 'team' : 'mine', q: search, page_size: 10 })).items); }
    catch (e) { setError(taskError(e)); }
  };
  const linkTask = async task => {
    try { await api.link(task.id, { operation_id: uuid(), expected_revision: task.revision, source }); setCandidates([]); load(); }
    catch (e) { setError(taskError(e)); }
  };
  const importLegacy = async () => {
    try { const result = await api.legacy(source, true); await load(); setEditor(result.task.id); }
    catch (e) { setError(taskError(e)); }
  };
  return <Box sx={{ border: '1px solid #ddd', borderRadius: 2, p: 1.5 }}>
    <Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>Teamaufgaben {data.total ? `(${data.total})` : ''}</Typography>
      {permissions.includes('work_tasks.create') && <Button onClick={() => setEditor('new')}>Aufgabe erstellen</Button>}</Stack>
    {error && <Alert severity="warning" action={<Button onClick={load}>Erneut prüfen</Button>}>{error}</Alert>}
    {loading ? <CircularProgress size={18} aria-label="Verknüpfte Aufgaben laden" /> : !error && !data.items.length && <Typography variant="body2">Keine verknüpften Aufgaben</Typography>}
    {!loading && data.items.map(task => <Button key={task.id} fullWidth sx={{ justifyContent: 'space-between' }} onClick={() => setEditor(task.id)}>{task.title}<span>{statuses[task.status]}</span></Button>)}
    {!loading && legacy?.task && Object.keys(legacy.task).length > 0 && !legacy.canonical_task_id && <Box sx={{ mt: 1 }}>
      <Typography>Bestehende Altaufgabe: {legacy.task.title || 'Aufgabe aus Gespräch'} ({legacy.task.status || 'open'})</Typography>
      {permissions.includes('work_tasks.manage') && <Button onClick={importLegacy}>Altaufgabe übernehmen</Button>}
    </Box>}
    {page > 1 && <Button onClick={() => setPage(value => value - 1)}>Zurück</Button>}{data.has_more && <Button onClick={() => setPage(value => value + 1)}>Weitere Aufgaben</Button>}
    {permissions.includes('work_tasks.read') && <Button href={`/admin/pmi-platform/tasks?source_kind=${encodeURIComponent(source.kind)}&source_id=${encodeURIComponent(source.source_id)}`}>Alle Aufgaben zu dieser Quelle</Button>}
    {permissions.includes('work_tasks.update') && <Box sx={{ mt: 1 }}><TextField size="small" label="Bestehende Aufgabe suchen" value={search} onChange={e => setSearch(e.target.value)} inputProps={{ maxLength: 200 }} /><Button onClick={findTasks}>Suchen</Button>
      {candidates.map(task => <Button key={task.id} fullWidth onClick={() => linkTask(task)}>{task.title} hier verknüpfen</Button>)}</Box>}
    {editor && <WorkTaskEditor open taskId={editor === 'new' ? null : editor} sources={[source, ...relatedSources]} initialTitle={initialTitle} onClose={() => setEditor(null)} onSaved={load} />}
  </Box>;
}

const sourceType = PropTypes.shape({
  kind: PropTypes.string.isRequired,
  source_id: PropTypes.string.isRequired,
});

WorkTaskPanel.propTypes = {
  source: sourceType.isRequired,
  relatedSources: PropTypes.arrayOf(sourceType),
  initialTitle: PropTypes.string,
};
